import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './DriversRank.css';
import DriverAssessmentPopup from '../DriverAssessmentPopup/DriverAssessmentPopup';
import tooltipTexts from '../../utils/tooltipTexts';
import { API_BASE_URL } from '../../api/apiClient';

const DriversRank = ({ vehicleId }) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [vehicleName, setVehicleName] = useState('');

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        // Get admin location from localStorage user object
        const user = JSON.parse(localStorage.getItem('user'));
        const locationId = user?.locationId;
        
        if (!locationId) {
          console.error('Admin location not found in localStorage');
          setLoading(false);
          return;
        }
        
        const response = await fetch(`${API_BASE_URL}/api/vehicle-leaderboard-admin?vehicleID=${vehicleId}&locationId=${locationId}`);
        if (!response.ok) throw new Error('Failed to fetch driver rankings');
        const data = await response.json();
        
        // Transform the data to match component requirements
        const formattedData = data.map((driver, index) => {
          const score = driver.AverageScore ? Math.round(driver.AverageScore) : Math.round(driver.UserPK);
          
          // Calculate stars based on score ranges:
          // 0-20=1 star, 20-40=2 stars, 40-60=3 stars, 60-80=4 stars, 80-100=5 stars
          let stars = 1; // Default to 1 star
          
          if (score >= 80) stars = 5;
          else if (score >= 60) stars = 4;
          else if (score >= 40) stars = 3;
          else if (score >= 20) stars = 2;
          
          return {
            rank: driver.Rankings,
            name: driver.EmployeeName,
            score: score,
            stars: stars,
            userPK: driver.UserPK,
            vehicleName: driver.VehicleName
          };
        });
        
        setDrivers(formattedData);
      } catch (error) {
        console.error('Error fetching driver rankings:', error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch the selected vehicle name
    const fetchVehicleName = async () => {
      try {
        if (!vehicleId) return;
        
        const user = JSON.parse(localStorage.getItem('user'));
        const locationId = user?.locationId;
        
        if (!locationId) return;
        
        const response = await fetch(`${API_BASE_URL}/api/vehicle-types?locationId=${locationId}`);
        if (!response.ok) throw new Error('Failed to fetch vehicle types');
        const data = await response.json();
        
        const selectedVehicle = data.find(v => v.VehicleID.toString() === vehicleId.toString());
        if (selectedVehicle) {
          // Use ScreenName if available, otherwise fall back to VehicleName
          setVehicleName(selectedVehicle.ScreenName || selectedVehicle.VehicleName);
        }
      } catch (error) {
        console.error('Error fetching vehicle name:', error);
      }
    };

    if (vehicleId) {
      fetchDrivers();
      fetchVehicleName();
    }
  }, [vehicleId]);

  const renderStars = (count) => {
    return '★'.repeat(count) + '☆'.repeat(5 - count);
  };

  const handleDriverClick = (driver) => {
    setSelectedDriver(driver);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  if (loading) {
    return <div className="drivers-rank">Loading driver rankings...</div>;
  }

  return (
    <div className="drivers-rank">
      <div className="chart-title">
        Drivers Rank As Score
        <span className="info-tooltip">
          <span className="info-icon">i</span>
          <span className="tooltip-text">{tooltipTexts.driversRank}</span>
        </span>
      </div>
      <table>
        <thead>
          <tr>
            <th>RANK</th>
            <th>NAME</th>
            <th>SCORE</th>
            <th>STAR</th>
          </tr>
        </thead>
        <tbody>
          {drivers.map((driver) => (
            <tr 
              key={`${driver.rank}-${driver.name}`} 
              onClick={() => handleDriverClick(driver)}
              className="driver-row"
            >
              <td>{driver.rank}.</td>
              <td>{driver.name}</td>
              <td>{driver.score}</td>
              <td className="star-rating">{renderStars(driver.stars)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {isPopupOpen && selectedDriver && (
        <DriverAssessmentPopup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          userPK={selectedDriver.userPK}
          userName={selectedDriver.name}
          vehicleName={vehicleName || selectedDriver.vehicleName}
          vehicleId={vehicleId}
        />
      )}
    </div>
  );
};

DriversRank.propTypes = {
  vehicleId: PropTypes.string.isRequired
};

export default DriversRank;